import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, LMStudioConfig } from '../types';
import { handleStreamResponse } from '../utils/StreamHandler';
import { parseErrorResponse } from '../utils/ErrorHandler';

interface LMStudioStreamResponse {
  response?: string;
  content?: string;
  choices?: Array<{
    text?: string;
    content?: string;
    delta?: {
      content?: string;
    };
    message?: {
      content?: string;
    };
  }>;
  done?: boolean;
}

export class LMStudioService extends BaseAIService {
  private config: LMStudioConfig;
  
  constructor(config: LMStudioConfig = {}) {
    super('lmstudio');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.apiUrl) {
      return {
        text: '',
        error: 'LMStudio 服务端点未配置'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      // 将消息直接用于chat格式
      const url = `${this.config.apiUrl}/chat/completions`;
      const headers = {
        'Content-Type': 'application/json'
      };
      
      const body = {
        messages: messages,
        stream: !!options.onUpdate,
        max_tokens: options.maxTokens || this.config.maxTokens,
        temperature: options.temperature ?? this.config.temperature ?? 0.7,
        top_p: options.topP ?? this.config.topP ?? 1,
        model: this.config.model || 'default'
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        // console.log('LMStudio: 使用流式响应');
        const text = await handleStreamResponse({
          url,
          headers,
          body,
          signal: options.abortSignal,
          onUpdate: options.onUpdate,
          errorPrefix: 'LMStudio 请求失败',
          parseContent: (data: unknown) => {
            const response = data as LMStudioStreamResponse;
            
            // 尝试多种可能的字段格式
            if (response.response) {
              return response.response;
            }
            
            if (response.content) {
              return response.content;
            }
            
            if (response.choices && response.choices.length > 0) {
              const choice = response.choices[0];
              if (choice.text) return choice.text;
              if (choice.content) return choice.content;
              if (choice.delta?.content) return choice.delta.content;
              if (choice.message?.content) return choice.message.content;
            }
            
            // 如果找不到有效数据，记录整个响应并返回空字符串
            console.warn('LMStudio: 未能从流式响应中提取文本', response);
            return '';
          }
        });
        
        return { text };
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response, 'LMStudio 请求失败');
        }
        
        const data = await response.json();
        return {
          text: data.choices?.[0]?.message?.content || data.choices?.[0]?.text || ''
        };
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          text: '',
          error: '请求已取消'
        };
      }
      return this.handleError(error);
    }
  }
} 